import { ThunkAction, ThunkDispatch } from 'redux-thunk'
import { AnyAction } from 'redux'
import { WikiDocument } from 'src/components/wiki/wikidocument/WikiDocument'
import { DataAction, EmptyAction, MessageAction } from 'src/CustomTypes'
import { ContextTag, WikiDocumentMeta, WikiLocation, WikiService } from 'src/dataSources/wiki/Wiki'
import { RootState } from 'src/Store'
import { WikiView } from 'src/reducers/WikiReducer'

export const FETCHING_DOCUMENTS_META_STARTED = 'WIKI_ACTIONS_FETCHING_DOCUMENTS_STARTED'
export const FETCHING_DOCUMENTS_META_FAILURE = 'WIKI_ACTIONS_FETCHING_DOCUMENTS_FAILURE'
export const FETCHING_DOCUMENTS_META_SUCCESS = 'WIKI_ACTIONS_FETCHING_DOCUMENTS_SUCCESS'
export const FETCHING_DOCUMENT_STARTED = 'WIKI_ACTIONS_FETCHING_DOCUMENT_STARTED'
export const FETCHING_DOCUMENT_FAILURE = 'WIKI_ACTIONS_FETCHING_DOCUMENT_FAILURE'
export const FETCHING_DOCUMENT_SUCCESS = 'WIKI_ACTIONS_FETCHING_DOCUMENT_SUCCESS'
export const ADD_OPEN_SECTION_ID = 'WIKI_ACTIONS_ADD_OPEN_SECTION_ID'
export const REMOVE_OPEN_SECTION_ID = 'WIKI_ACTIONS_REMOVE_OPEN_SECTION_ID'
export const ADD_CONTEXT_TAGS = 'WIKI_ACTIONS_ADD_CONTEXT_TAGS'
export const REMOVE_CONTEXT_TAGS = 'WIKI_ACTIONS_REMOVE_CONTEXT_TAGS'
export const ADD_CATEGORIES = 'WIKI_ACTIONS_ADD_CATEGORIES'
export const SET_POINTS_OF_INTEREST = 'WIKI_ACTIONS_SET_POINTS_OF_INTEREST'
export const SET_CATEGORY = 'WIKI_ACTIONS_SET_CATEGORY'
export const PUSH_VIEW = 'WIKI_ACTIONS_PUSH_VIEW'
export const POP_VIEW = 'WIKI_ACTIONS_POP_VIEW'
export const REMOVE_VIEW = 'WIKI_ACTIONS_REMOVE_VIEW'
export const CLEAR_VIEW_STACK = 'WIKI_ACTIONS_CLEAR_VIEW_STACK'
export const SHOW_WIKI_SIDE_BAR = 'WIKI_ACTIONS_SHOW_WIKI_SIDE_BAR'
export const CLEAR_DOCUMENTS_META = 'WIKI_ACTIONS_CLEAR_DOCUMENTS_META'
export const CLEAR_DOCUMENT = 'WIKI_ACTIONS_CLEAR_DOCUMENT'

interface WikiActions {
  fetchingDocumentsMetaStarted: () => EmptyAction
  fetchingDocumentsMetaFailure: (message: string) => MessageAction
  fetchingDocumentsMetaSuccess: (documentsMeta: WikiDocumentMeta[]) => DataAction<WikiDocumentMeta[]>
  fetchingDocumentStarted: () => EmptyAction
  fetchingDocumentFailure: (message: string) => MessageAction
  fetchingDocumentSuccess: (document: WikiDocument) => DataAction<WikiDocument>
  addOpenSectionId: (id: string) => DataAction<string>
  removeOpenSectionId: (id: string) => DataAction<string>
  addContextTags: (contextTags: ContextTag[]) => DataAction<ContextTag[]>
  removeContextTags: (contextTags: ContextTag[]) => DataAction<ContextTag[]>
  addCategories: (categories: string[]) => DataAction<string[]>
  setPointsOfInterest: (poi: WikiLocation[]) => DataAction<WikiLocation[]>
  setCategory: (category: string) => DataAction<string>
  pushView: (wikiView: WikiView) => DataAction<WikiView>
  popView: () => EmptyAction
  removeView: (wikiView: WikiView) => DataAction<WikiView>
  clearViewStack: () => EmptyAction
  showWikiSideBar: (show: boolean) => DataAction<boolean>
  clearDocumentsMeta: () => EmptyAction
  clearDocument: () => EmptyAction

  // Thunk actions
  clearViews: () => ThunkAction<void, RootState, {}, AnyAction>
  fetchDocumentsMeta: () => ThunkAction<Promise<void>, RootState, {}, AnyAction>
  fetchDocument: (documentId: string) => ThunkAction<Promise<void>, RootState, {}, AnyAction>
  openCategory: (category: string) => ThunkAction<Promise<void>, RootState, {}, AnyAction>
}

export const wikiActions: WikiActions = {
  fetchingDocumentsMetaStarted: (): EmptyAction => {
    return {
      type: FETCHING_DOCUMENTS_META_STARTED
    }
  },
  fetchingDocumentsMetaFailure: (message: string): MessageAction => {
    return {
      type: FETCHING_DOCUMENTS_META_FAILURE,
      message: message
    }
  },
  fetchingDocumentsMetaSuccess: (documentsMeta: WikiDocumentMeta[]): DataAction<WikiDocumentMeta[]> => {
    return {
      type: FETCHING_DOCUMENTS_META_SUCCESS,
      data: documentsMeta
    }
  },
  fetchingDocumentStarted: (): EmptyAction => {
    return {
      type: FETCHING_DOCUMENT_STARTED
    }
  },
  fetchingDocumentFailure: (message: string): MessageAction => {
    return {
      type: FETCHING_DOCUMENT_FAILURE,
      message: message
    }
  },
  fetchingDocumentSuccess: (document: WikiDocument): DataAction<WikiDocument> => {
    return {
      type: FETCHING_DOCUMENT_SUCCESS,
      data: document
    }
  },
  addOpenSectionId: (id: string): DataAction<string> => {
    return {
      type: ADD_OPEN_SECTION_ID,
      data: id
    }
  },
  removeOpenSectionId: (id: string): DataAction<string> => {
    return {
      type: REMOVE_OPEN_SECTION_ID,
      data: id
    }
  },
  addContextTags: (contextTags: ContextTag[]): DataAction<ContextTag[]> => {
    return {
      type: ADD_CONTEXT_TAGS,
      data: contextTags
    }
  },
  removeContextTags: (contextTags: ContextTag[]): DataAction<ContextTag[]> => {
    return {
      type: REMOVE_CONTEXT_TAGS,
      data: contextTags
    }
  },
  addCategories: (categories: string[]): DataAction<string[]> => {
    return {
      type: ADD_CATEGORIES,
      data: categories
    }
  },
  setPointsOfInterest: (poi: WikiLocation[]): DataAction<WikiLocation[]> => {
    return {
      type: SET_POINTS_OF_INTEREST,
      data: poi
    }
  },
  setCategory: (category: string): DataAction<string> => {
    return {
      type: SET_CATEGORY,
      data: category
    }
  },
  pushView: (wikiView: WikiView): DataAction<WikiView> => {
    return {
      type: PUSH_VIEW,
      data: wikiView
    }
  },
  popView: (): EmptyAction => {
    return {
      type: POP_VIEW
    }
  },
  removeView: (wikiView: WikiView): DataAction<WikiView> => {
    return {
      type: REMOVE_VIEW,
      data: wikiView
    }
  },
  clearViewStack: (): EmptyAction => {
    return {
      type: CLEAR_VIEW_STACK
    }
  },
  showWikiSideBar: (show: boolean): DataAction<boolean> => {
    return {
      type: SHOW_WIKI_SIDE_BAR,
      data: show
    }
  },
  clearDocumentsMeta: (): EmptyAction => {
    return {
      type: CLEAR_DOCUMENTS_META
    }
  },
  clearDocument: (): EmptyAction => {
    return {
      type: CLEAR_DOCUMENT
    }
  },
  clearViews: (): ThunkAction<void, RootState, {}, AnyAction> => {
    return (dispatch: ThunkDispatch<RootState, {}, AnyAction>, getState: () => RootState): void => {
      dispatch(wikiActions.clearViewStack())
      dispatch(wikiActions.clearDocument())
      dispatch(wikiActions.showWikiSideBar(false))
    }
  },
  fetchDocumentsMeta: (): ThunkAction<Promise<void>, RootState, {}, AnyAction> => {
    return async (dispatch: ThunkDispatch<RootState, {}, AnyAction>, getState: () => RootState): Promise<void> => {
      dispatch(wikiActions.clearDocumentsMeta())
      dispatch(wikiActions.fetchingDocumentsMetaStarted())
      const wikiService = new WikiService()
      let documentsMeta: WikiDocumentMeta[]
      try {
        documentsMeta = await wikiService.fetchDocumentsMeta()

        const categories: string[] = []
        const poi: WikiLocation[] = []
        documentsMeta.forEach(meta => {
          meta.categories?.forEach(category => {
            if (!categories.includes(category)) {
              categories.push(category)
            }
          })
          if ('location' in meta) {
            let location = meta.location!
            location.key = meta.key
            poi.push(location)
          }
        })
        dispatch(wikiActions.addCategories(categories))
        dispatch(wikiActions.setPointsOfInterest(poi))

        dispatch(wikiActions.fetchingDocumentsMetaSuccess(documentsMeta))
      } catch (err) {
        console.error(err)
        const errorMessage: string = (err as Error).message
        dispatch(wikiActions.fetchingDocumentsMetaFailure(errorMessage))
      }
    }
  },
  fetchDocument: (documentId: string): ThunkAction<Promise<void>, RootState, {}, AnyAction> => {
    return async (dispatch: ThunkDispatch<RootState, {}, AnyAction>, getState: () => RootState): Promise<void> => {
      dispatch(wikiActions.clearDocument())
      dispatch(wikiActions.removeView(WikiView.WikiDocument))
      dispatch(wikiActions.pushView(WikiView.WikiDocument))
      dispatch(wikiActions.showWikiSideBar(true))
      dispatch(wikiActions.fetchingDocumentStarted())
      const wikiService = new WikiService()
      try {
        const document = await wikiService.fetchDocument(documentId)
        dispatch(wikiActions.fetchingDocumentSuccess(document))
      } catch (err) {
        console.error(err)
        const errorMessage: string = (err as Error).message
        dispatch(wikiActions.fetchingDocumentFailure(errorMessage))
      }
    }
  },
  openCategory: (category: string): ThunkAction<Promise<void>, RootState, {}, AnyAction> => {
    return async (dispatch: ThunkDispatch<RootState, {}, AnyAction>, getState: () => RootState): Promise<void> => {
      dispatch(wikiActions.setCategory(category))
      dispatch(wikiActions.pushView(WikiView.WikiCategory))
      dispatch(wikiActions.showWikiSideBar(true))
    }
  }
}
